/**
 * Route table. Every screen is a lazy chunk (`lazyRouteComponent`) so the
 * entry bundle carries only the shell — the canvas editor, University, Agent
 * Detail etc. load on first navigation. Keep it that way: a static screen
 * import here puts that screen back into everyone's first paint.
 */
import {
  createRootRoute,
  createRoute,
  createRouter,
  lazyRouteComponent,
  Navigate,
  Outlet,
} from '@tanstack/react-router';
import { Splash } from './components/Splash.js';

const rootRoute = createRootRoute({
  component: () => <Outlet />,
});

const homeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  component: lazyRouteComponent(() => import('./routes/HomeRoute.js'), 'HomeRoute'),
});

const inboxRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/inbox',
  component: lazyRouteComponent(() => import('./routes/InboxRoute.js'), 'InboxRoute'),
});

const agentsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/agents',
  component: lazyRouteComponent(() => import('./routes/AgentsRoute.js'), 'AgentsRoute'),
});

const createAgentRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/create-agent',
  component: lazyRouteComponent(() => import('./routes/CreateAgentRoute.js'), 'CreateAgentRoute'),
  validateSearch: (s: Record<string, unknown>): { q?: string } => ({
    q: typeof s.q === 'string' ? s.q : undefined,
  }),
});

const agentDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/agents/$agentId',
  component: lazyRouteComponent(() => import('./routes/AgentDetailRoute.js'), 'AgentDetailRoute'),
  validateSearch: (s: Record<string, unknown>): { chat?: string; q?: string } => ({
    chat: typeof s.chat === 'string' ? s.chat : undefined,
    q: typeof s.q === 'string' ? s.q : undefined,
  }),
});

const canvasBootstrapRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/canvas',
  component: lazyRouteComponent(
    () => import('./routes/CanvasBootstrapRoute.js'),
    'CanvasBootstrapRoute',
  ),
  validateSearch: (s: Record<string, unknown>): { q?: string } => ({
    q: typeof s.q === 'string' ? s.q : undefined,
  }),
});

const canvasRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/p/$processId',
  component: lazyRouteComponent(() => import('./routes/CanvasRoute.js'), 'CanvasRoute'),
  validateSearch: (s: Record<string, unknown>): { q?: string } => ({
    q: typeof s.q === 'string' ? s.q : undefined,
  }),
});

const processesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/processes',
  component: lazyRouteComponent(() => import('./routes/ProcessLibraryRoute.js'), 'ProcessLibraryRoute'),
});
const processDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/processes/$processId',
  component: lazyRouteComponent(() => import('./routes/ProcessDetailRoute.js'), 'ProcessDetailRoute'),
});
const liveCaseRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/cases/$caseId',
  component: lazyRouteComponent(() => import('./routes/LiveCaseRoute.js'), 'LiveCaseRoute'),
});

/** Remaining rail destinations (all real screens now). */
const myWorkRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/my-work',
  component: lazyRouteComponent(() => import('./routes/MyWorkRoute.js'), 'MyWorkRoute'),
});
const groupsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/groups',
  component: lazyRouteComponent(() => import('./routes/GroupsRoute.js'), 'GroupsRoute'),
});
const signalsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/signals',
  component: lazyRouteComponent(() => import('./routes/SignalsRoute.js'), 'SignalsRoute'),
});
const monitorRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/monitor',
  component: lazyRouteComponent(() => import('./routes/MonitorRoute.js'), 'MonitorRoute'),
});
const recordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/record',
  component: lazyRouteComponent(() => import('./routes/RecordRoute.js'), 'RecordRoute'),
  // The whole state of the Record is its as-of pin, so a view of the record at a
  // date is a shareable link rather than something you have to re-drive by hand.
  validateSearch: (s: Record<string, unknown>): { asOf?: string; view?: string; edge?: string } => ({
    asOf: typeof s.asOf === 'string' ? s.asOf : undefined,
    view: typeof s.view === 'string' ? s.view : undefined,
    edge: typeof s.edge === 'string' ? s.edge : undefined,
  }),
});

const knowledgeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/knowledge',
  component: lazyRouteComponent(() => import('./routes/KnowledgeRoute.js'), 'KnowledgeRoute'),
  // Both pins, the base and the graph scope live in the URL, so a governed view —
  // this base, in force on one date, as we knew it on another — is a link you send
  // rather than something the next person has to re-drive by hand. `eff`/`known`
  // rather than `asOf`/`asAt`: two names differing by one character is a real hazard
  // when a presenter reads a link off a slide.
  validateSearch: (
    s: Record<string, unknown>,
  ): {
    base?: string;
    tab?: string;
    eff?: string;
    known?: string;
    node?: string;
    depth?: string;
  } => ({
    base: typeof s.base === 'string' ? s.base : undefined,
    tab: typeof s.tab === 'string' ? s.tab : undefined,
    eff: typeof s.eff === 'string' ? s.eff : undefined,
    known: typeof s.known === 'string' ? s.known : undefined,
    node: typeof s.node === 'string' ? s.node : undefined,
    depth: typeof s.depth === 'string' ? s.depth : undefined,
  }),
});

const universityRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/university',
  component: lazyRouteComponent(() => import('./routes/UniversityRoute.js'), 'UniversityRoute'),
});

/* CEO governance board — persona-gated menu for the Atlas Health CEO. Each is a
 * lazy chunk; unreachable from any org/persona except the Atlas Health CEO. */
const ceoBoardRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/ceo',
  component: lazyRouteComponent(() => import('./routes/CeoBoardRoute.js'), 'CeoBoardRoute'),
});
const ceoExploreRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/ceo/explore',
  component: lazyRouteComponent(() => import('./routes/CeoExploreRoute.js'), 'CeoExploreRoute'),
});
const ceoInsightsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/ceo/insights',
  component: lazyRouteComponent(() => import('./routes/CeoInsightsRoute.js'), 'CeoInsightsRoute'),
});
const ceoWorkforceRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/ceo/workforce',
  component: lazyRouteComponent(() => import('./routes/CeoWorkforceRoute.js'), 'CeoWorkforceRoute'),
});
const ceoObjectivesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/ceo/objectives',
  component: lazyRouteComponent(() => import('./routes/CeoObjectivesRoute.js'), 'CeoObjectivesRoute'),
});
const ceoPoliciesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/ceo/policies',
  component: lazyRouteComponent(() => import('./routes/CeoPoliciesRoute.js'), 'CeoPoliciesRoute'),
});
const ceoEscalationsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/ceo/escalations',
  component: lazyRouteComponent(() => import('./routes/CeoEscalationsRoute.js'), 'CeoEscalationsRoute'),
});
const ceoAuditRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/ceo/audit',
  component: lazyRouteComponent(() => import('./routes/CeoAuditRoute.js'), 'CeoAuditRoute'),
});

const routeTree = rootRoute.addChildren([
  homeRoute,
  inboxRoute,
  agentsRoute,
  createAgentRoute,
  agentDetailRoute,
  canvasBootstrapRoute,
  canvasRoute,
  processesRoute,
  processDetailRoute,
  liveCaseRoute,
  myWorkRoute,
  groupsRoute,
  signalsRoute,
  monitorRoute,
  universityRoute,
  recordRoute,
  knowledgeRoute,
  ceoBoardRoute,
  ceoExploreRoute,
  ceoInsightsRoute,
  ceoWorkforceRoute,
  ceoObjectivesRoute,
  ceoPoliciesRoute,
  ceoEscalationsRoute,
  ceoAuditRoute,
]);

export const router = createRouter({
  routeTree,
  defaultPendingComponent: () => <Splash label="Loading…" />,
  // A hand-authored href that resolves to nothing used to render a blank screen.
  // Send it Home instead — on stage a mistyped link should cost a click, not the demo.
  defaultNotFoundComponent: () => <Navigate to="/" />,
  // Preload a screen's chunk when its link is hovered/focused — first
  // navigation feels instant without shipping everything up front.
  defaultPreload: 'intent',
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
